const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

import { loadExperts, loadPersons } from './listsService';

/**
 * Интерфейс для ответа API статистики экспертов
 */
interface ExpertStatResponse {
  message: string;
  expertStat: boolean;
  timestamp: string;
  requestId: string;
}

/**
 * Результат валидации эксперта или личности
 */
interface ValidationResult {
  isValid: boolean;
  type: 'expert' | 'person' | 'none';
  value: string;
}

/**
 * Принудительно загружает актуальные списки экспертов и личностей для указанного языка
 */
export async function forceReloadLists(lang: string | undefined): Promise<{ experts: any[]; persons: any[] }> {
  console.log('🔄 Принудительно загружаем актуальные списки экспертов и личностей...');
  
  try {
    const [experts, persons] = await Promise.all([
      loadExperts(lang, true),
      loadPersons(lang, true)
    ]);
    
    console.log(`✅ Загружено ${experts.length} экспертов и ${persons.length} личностей`);
    
    return { experts, persons };
  } catch (error) {
    console.error('❌ Ошибка принудительной загрузки списков:', error);
    throw error;
  }
}

/**
 * Проверяет, соответствует ли выбранный эксперт или личность актуальному списку
 */
export function validateExpertOrPerson(
  selectedValue: string | undefined,
  selectedType: 'expert' | 'person' | 'none',
  customValue: string | undefined,
  experts: any[],
  persons: any[]
): ValidationResult {
  // Если ничего не выбрано
  if (!selectedValue && !customValue) {
    return {
      isValid: false, // Изменено: если никто не выбран, то expertStat = false
      type: 'none',
      value: ''
    };
  }
  
  // Определяем финальное значение
  let finalValue = selectedValue;
  if (selectedValue === 'custom' && customValue) {
    finalValue = customValue.trim();
  }
  
  if (!finalValue) {
    return {
      isValid: true,
      type: 'none',
      value: ''
    };
  }
  
  // Проверяем соответствие в зависимости от типа
  if (selectedType === 'expert') {
    const isValid = experts.some(expert => 
      expert.value === finalValue || 
      expert.label.toLowerCase() === finalValue.toLowerCase()
    );
    
    return {
      isValid,
      type: 'expert',
      value: finalValue
    };
  } else if (selectedType === 'person') {
    const isValid = persons.some(person => 
      person.value === finalValue || 
      person.label.toLowerCase() === finalValue.toLowerCase()
    );
    
    return {
      isValid,
      type: 'person',
      value: finalValue
    };
  }
  
  // Если тип не определен, проверяем в обоих списках
  const isExpert = experts.some(expert => 
    expert.value === finalValue || 
    expert.label.toLowerCase() === finalValue.toLowerCase()
  );
  
  const isPerson = persons.some(person => 
    person.value === finalValue || 
    person.label.toLowerCase() === finalValue.toLowerCase()
  );
  
  return {
    isValid: isExpert || isPerson,
    type: isExpert ? 'expert' : isPerson ? 'person' : 'none',
    value: finalValue
  };
}

/**
 * Отправляет результат валидации на сервер
 */
export async function sendExpertStat(expertStat: boolean): Promise<ExpertStatResponse> {
  console.log(`📊 Отправляем статистику экспертов: ${expertStat}`);
  
  try {
    const response = await fetch(`${SHARE_SERVER_URL}/api/experts/stat`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ expertStat })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ExpertStatResponse = await response.json();
    console.log(`✅ Статистика экспертов отправлена:`, data);
    
    return data;
  } catch (error) {
    console.error('❌ Ошибка отправки статистики экспертов:', error);
    throw error;
  }
}

/**
 * Полная валидация эксперта или личности с отправкой статистики
 * @param lang — язык для загрузки списков (ru/en), по умолчанию нормализуется в сервисе
 */
export async function validateAndSendExpertStat(
  selectedExpert: string | undefined,
  selectedPerson: string | undefined,
  customExpert: string | undefined,
  customPerson: string | undefined,
  lang?: string
): Promise<ValidationResult> {
  try {
    // 1. Принудительно загружаем актуальные списки для выбранного языка
    const { experts, persons } = await forceReloadLists(lang);
    
    // 2. Определяем тип выбора (эксперт или личность)
    let selectedType: 'expert' | 'person' | 'none' = 'none';
    let selectedValue: string | undefined;
    let customValue: string | undefined;
    
    if (selectedExpert || customExpert) {
      selectedType = 'expert';
      selectedValue = selectedExpert;
      customValue = customExpert;
    } else if (selectedPerson || customPerson) {
      selectedType = 'person';
      selectedValue = selectedPerson;
      customValue = customPerson;
    }
    
    // 3. Проверяем соответствие
    const validationResult = validateExpertOrPerson(
      selectedValue,
      selectedType,
      customValue,
      experts,
      persons
    );
    
    // 4. Отправляем статистику
    await sendExpertStat(validationResult.isValid);
    
    console.log(`🔍 Результат валидации:`, {
      isValid: validationResult.isValid,
      type: validationResult.type,
      value: validationResult.value
    });
    
    return validationResult;
  } catch (error) {
    console.error('❌ Ошибка валидации эксперта/личности:', error);
    
    // В случае ошибки отправляем false
    try {
      await sendExpertStat(false);
    } catch (statError) {
      console.error('❌ Ошибка отправки статистики при ошибке валидации:', statError);
    }
    
    // Возвращаем невалидный результат
    return {
      isValid: false,
      type: 'none',
      value: ''
    };
  }
}
